import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import RobotIcon from "./RobotIcon";

// Icons via simple inline svg paths to avoid extra deps.
const TOOLS = [
  // AI
  { key: "ai_studio", to: "/tools/ai/studio", group: "ai", color: "#3E63DD", robot: true },
  { key: "natural_command", to: "/tools/ai/command", group: "ai", color: "#3E63DD",
    icon: "M4 6h16 M4 12h12 M4 18h8 M19 14l3 3-3 3" },
  { key: "docgen", to: "/tools/docgen", group: "ai", color: "#7C3AED",
    icon: "M5 4h10l4 4v12H5z M9 12h6 M9 16h4 M14 4v4h4" },
  // Convert
  { key: "scan", to: "/tools/scan", group: "core", color: "#7C3AED",
    icon: "M3 7V5a2 2 0 0 1 2-2h2 M17 3h2a2 2 0 0 1 2 2v2 M21 17v2a2 2 0 0 1-2 2h-2 M7 21H5a2 2 0 0 1-2-2v-2 M8 10h8 M8 14h5" },
  { key: "convert", to: "/tools/convert", group: "core", color: "#09090B",
    icon: "M4 4h12l4 4v12H4z M16 4v4h4" },
  { key: "translate", to: "/tools/translate", group: "core", color: "#3E63DD",
    icon: "M4 6h6 M4 10h6 M7 4v2 M14 14h6 M14 18h6 M17 12v2 M10 4l-3 8h2l1-3h2l1 3h2L12 4z" },
  // PDF tools
  { key: "merge", to: "/tools/pdf/merge", group: "pdf", color: "#EF4444",
    icon: "M9 4h6v6h6v6h-6v4H9v-4H3v-6h6z" },
  { key: "split", to: "/tools/pdf/split", group: "pdf", color: "#EF4444",
    icon: "M12 3v18 M5 8h14 M5 16h14" },
  { key: "extract", to: "/tools/pdf/extract", group: "pdf", color: "#EF4444",
    icon: "M5 4h10l4 4v12H5z M9 12h6 M9 16h4" },
  { key: "reorder", to: "/tools/pdf/reorder", group: "pdf", color: "#EF4444",
    icon: "M3 6h13l-3-3 m3 3-3 3 M21 18H8l3 3 m-3-3 3-3" },
  { key: "sign", to: "/tools/pdf/sign", group: "pdf", color: "#2563EB",
    icon: "M3 17l4 4L21 7l-2-2L7 17 M14 6l4 4" },
  { key: "annotate", to: "/tools/pdf/annotate", group: "pdf", color: "#F97316",
    icon: "M3 21l3-1 12-12-3-3-12 12-1 3z M14 6l3 3" },
  // Privacy
  { key: "ocr", to: "/tools/pdf/ocr", group: "privacy", color: "#16A34A",
    icon: "M3 3h6v6H3z M15 3h6v6h-6z M3 15h6v6H3z M15 15h6v6h-6z" },
  { key: "compress", to: "/tools/pdf/compress", group: "privacy", color: "#16A34A",
    icon: "M5 9V5h4 M19 9V5h-4 M5 15v4h4 M19 15v4h-4 M9 12h6" },
  { key: "protect", to: "/tools/pdf/protect", group: "privacy", color: "#0EA5E9",
    icon: "M5 11V8a7 7 0 0 1 14 0v3 M5 11h14v10H5z M12 15v3" },
  { key: "watermark", to: "/tools/pdf/watermark", group: "privacy", color: "#F59E0B",
    icon: "M3 7h18 M3 12h18 M3 17h18" },
  { key: "redact", to: "/tools/pdf/redact", group: "privacy", color: "#DC2626",
    icon: "M3 12h18 M6 6h12 M6 18h12" },
  // Share
  { key: "share_sign", to: "/tools/share-sign", group: "share", color: "#2563EB",
    icon: "M4 12v5h16v-5 M12 4v10 M8 8l4-4 4 4" },
  // Recipes
  { key: "recipes", to: "/tools/recipes", group: "share", color: "#09090B",
    icon: "M4 4h16v4H4z M4 10h16v4H4z M4 16h16v4H4z" },
];

const GROUPS = [
  { id: "ai", label_key: "group_ai" },
  { id: "core", label_key: "group_core" },
  { id: "pdf", label_key: "group_pdf" },
  { id: "privacy", label_key: "group_privacy" },
  { id: "share", label_key: "group_share" },
];

export default function ToolGrid({ filter }) {
  const { t } = useTranslation();
  const grouped = GROUPS.map((g) => ({
    ...g,
    items: TOOLS.filter((tool) => tool.group === g.id && (!filter || filter === g.id)),
  })).filter((g) => g.items.length);

  return (
    <div className="space-y-12" data-testid="tool-grid">
      {grouped.map((g) => (
        <div key={g.id}>
          <div className="flex items-end justify-between mb-5">
            <span className="eyebrow text-zinc-500" data-testid={`group-${g.id}`}>
              {t(g.label_key)}
            </span>
            <div className="flex-1 h-px bg-zinc-200 ml-6" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {g.items.map((tool, i) => (
              <Link
                key={tool.key}
                to={tool.to}
                className="tool-card p-6 rounded-md group fade-up"
                style={{ animationDelay: `${i * 40}ms` }}
                data-testid={`tool-card-${tool.key}`}
              >
                <div className="flex items-start justify-between mb-8">
                  <div className="w-10 h-10 grid place-items-center" style={{ background: tool.color }}>
                    {tool.robot ? (
                      <RobotIcon size={20} stroke="white" strokeWidth={2.2} fill="white" />
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                        <path d={tool.icon} />
                      </svg>
                    )}
                  </div>
                  <span className="eyebrow">{tool.group.toUpperCase()}</span>
                </div>
                <div className="hero-h2 text-xl mb-2">{t(`tool_${tool.key}`)}</div>
                <div className="text-sm text-zinc-500 leading-relaxed">{t(`tool_${tool.key}_desc`)}</div>
                <div className="mt-6 flex items-center text-xs font-medium tracking-wider uppercase">
                  <span className="group-hover:translate-x-1 transition-transform">→</span>
                  <span className="ml-2 group-hover:underline">{t("cta_continue")}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
